import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FormSelect, FormSelectOption, StyledButton } from '@tryhackme/thm-ui-components';
import { useMediaQuery } from 'src/common/hooks/use-media-query';
import { BREAKPOINTS } from 'src/common/constants/breakpoints';
import { useGetUserQuery, useTrackEventMutation } from 'src/common/slices';
import { useScenarioPermissions } from 'src/common/hooks';
import { ROLE_OPTIONS } from '../../skills-matrix.constants';
import { RoleButton } from '../role-button';
import { UpgradeToPremiumModal } from '../upgrade-to-premium-modal';
import { Content } from '../content';
import { RoleProps } from '../../skills-matrix.types';
import { StyledButtonsWrapper, StyledGridWrapper } from './matrix-content.styles';
export const SkillsMatrixContent = () => {
  const [selectedRole, setSelectedRole] = useState('foundational');
  const [selectedRoleOption, setSelectedRoleOption] = useState(ROLE_OPTIONS[0]);
  const [openIsPremiumModal, setOpenIsPremiumModal] = useState(false);
  const [trackEvent] = useTrackEventMutation();
  const {
    data: userData
  } = useGetUserQuery();
  const {
    hasAccessToB2B
  } = useScenarioPermissions();
  const isAvailableForUser = userData?.data?.user?.isPremium || hasAccessToB2B;
  const isDesktop = useMediaQuery(`(min-width: ${BREAKPOINTS.DESKTOP}px)`);
  useEffect(() => {
    const savedRole = localStorage.getItem('selectedRoleSkills');
    if (savedRole) {
      setSelectedRole(savedRole);
    }
  }, []);
  useEffect(() => {
    if (isAvailableForUser) {
      const storedRole = localStorage.getItem('selectedRoleSkills') || 'foundational';
      const storedRoleOption = ROLE_OPTIONS.find(role => role.id === storedRole);
      if (storedRoleOption) {
        setSelectedRoleOption(storedRoleOption);
        setSelectedRole(storedRole);
      }
    } else {
      setSelectedRole('foundational');
      setSelectedRoleOption(ROLE_OPTIONS[0]);
    }
  }, [isAvailableForUser]);
  const getEventForRole = (role: string) => {
    switch (role) {
      case 'foundational':
        {
          return trackEvent({
            event: 'click-foundational-role'
          });
        }
      case 'analyst':
        {
          return trackEvent({
            event: 'click-security-analyst-role'
          });
        }
      case 'penetration':
        {
          return trackEvent({
            event: 'click-pentest-role'
          });
        }
      case 'engineer':
        {
          return trackEvent({
            event: 'click-security-eng-role'
          });
        }
      default:
        {
          return trackEvent({
            event: 'click-foundational-role'
          });
        }
    }
  };
  const handleSelectionChange = (selectedOption: FormSelectOption) => {
    if (isAvailableForUser) {
      setSelectedRoleOption((selectedOption as RoleProps));
      if (selectedOption) {
        const newRole = selectedOption.id ?? 'foundational';
        setSelectedRole(newRole);
        getEventForRole(newRole).catch(() => {});
        localStorage.setItem('selectedRoleSkills', newRole);
      }
    } else {
      setOpenIsPremiumModal(true);
      setSelectedRole('foundational');
      localStorage.setItem('selectedRoleSkills', 'foundational');
    }
  };
  const handleRoleButton = (roleId: string) => {
    if (isAvailableForUser) {
      setSelectedRole(roleId);
      getEventForRole(roleId).catch(() => {});
      localStorage.setItem('selectedRoleSkills', roleId);
    } else {
      setOpenIsPremiumModal(true);
      setSelectedRole('foundational');
      localStorage.setItem('selectedRoleSkills', 'foundational');
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const currentRole = ROLE_OPTIONS.find(role => role.id === selectedRole) ?? ROLE_OPTIONS[0]!;
  return <StyledGridWrapper gridTemplateColumns="299px 1fr" gap="0" data-sentry-element="StyledGridWrapper" data-sentry-component="SkillsMatrixContent" data-sentry-source-file="matrix-content.tsx">
      {!isDesktop && <span data-type="select">
          <FormSelect isMulti={false} isSearchable={false} options={(ROLE_OPTIONS as FormSelectOption[])} selected={(selectedRoleOption as FormSelectOption)} handleSelection={handleSelectionChange} data-testid="select-role" />
        </span>}
      {isDesktop && <StyledButtonsWrapper>
          <ul>
            {ROLE_OPTIONS.map(role => <li key={role.id}>
                <RoleButton icon={role.icon} label={role.label} isButtonSelected={selectedRole === role.id} onClick={() => handleRoleButton(role.id)} data-testid={`button-${role.id}`} isPremium={isAvailableForUser} />
              </li>)}
          </ul>
          <p>
            Try our{' '}
            <StyledButton as={Link} to="/careers/quiz" color="info" variant="text" target="_blank">
              career quiz
            </StyledButton>{' '}
            to find a role that fits
          </p>
        </StyledButtonsWrapper>}
      <UpgradeToPremiumModal isModalOpen={openIsPremiumModal} setIsModalOpen={setOpenIsPremiumModal} data-sentry-element="UpgradeToPremiumModal" data-sentry-source-file="matrix-content.tsx" />
      <Content role={currentRole} data-sentry-element="Content" data-sentry-source-file="matrix-content.tsx" />
    </StyledGridWrapper>;
};